/**
 * Profile Image Cache Utility
 * 
 * Stores profile images locally in the browser using localStorage.
 * This ensures profile pictures persist across app deployments and reloads.
 */

import { getAccessToken } from '@/config/api';

const PROFILE_IMAGE_PREFIX = 'profileImage_';
const PROFILE_IMAGE_TIMESTAMP_PREFIX = 'profileImageTimestamp_';
const CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

async function getProfileImageHeaders(): Promise<HeadersInit | null> {
  try {
    const token = await getAccessToken();
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
      };
    }
  } catch {
    // Token acquisition failed.
  }

  return null;
}

/**
 * Converts a blob/image data URL to base64 string
 */
export const imageToBase64 = async (imageUrl: string): Promise<string> => {
  try {
    const response = await fetch(imageUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    if (blob.size === 0) {
      throw new Error('Received empty image blob');
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    throw error;
  }
};

/**
 * Save profile image to localStorage
 * @param userId - User identifier
 * @param imageDataUrl - Base64 encoded image data URL
 */
export const saveProfileImage = (userId: string, imageDataUrl: string): void => {
  const key = `${PROFILE_IMAGE_PREFIX}${userId}`;
  const timestampKey = `${PROFILE_IMAGE_TIMESTAMP_PREFIX}${userId}`;

  try {
    localStorage.setItem(key, imageDataUrl);
    localStorage.setItem(timestampKey, Date.now().toString());
    
    // Profile image cached (removed verbose logging)
  } catch (error) {
    console.error('Error saving profile image to localStorage:', error);
    // If localStorage is full, try to clear old profile images
    clearOldProfileImages();
    try {
      localStorage.setItem(key, imageDataUrl);
      localStorage.setItem(timestampKey, Date.now().toString());
    } catch (retryError) {
      console.error('Failed to save profile image after cleanup:', retryError);
    }
  }
};

/**
 * Get profile image from localStorage
 * @param userId - User identifier
 * @returns Base64 encoded image data URL or null if not found/expired
 */
export const getProfileImage = (userId: string): string | null => {
  try {
    const key = `${PROFILE_IMAGE_PREFIX}${userId}`;
    const timestampKey = `${PROFILE_IMAGE_TIMESTAMP_PREFIX}${userId}`;
    
    const imageDataUrl = localStorage.getItem(key);
    const timestamp = localStorage.getItem(timestampKey);
    
    // getProfileImage: debug info removed
    
    if (!imageDataUrl || !timestamp) {
      return null;
    }
    
    // Check if cache is expired
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_DURATION_MS) {
      // console.log(`Profile image cache expired for user: ${userId}`);
      removeProfileImage(userId);
      return null;
    }
    
    return imageDataUrl;
  } catch (error) {
    console.error('Error retrieving profile image from localStorage:', error);
    return null;
  }
};

/**
 * Remove profile image from localStorage
 * @param userId - User identifier
 */
export const removeProfileImage = (userId: string): void => {
  try {
    localStorage.removeItem(`${PROFILE_IMAGE_PREFIX}${userId}`);
    localStorage.removeItem(`${PROFILE_IMAGE_TIMESTAMP_PREFIX}${userId}`);
    // console.log(`Profile image removed for user: ${userId}`);
  } catch (error) {
    console.error('Error removing profile image:', error);
  }
};

/**
 * Clear all profile images from localStorage
 */
export const clearAllProfileImages = (): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(PROFILE_IMAGE_PREFIX) || key.startsWith(PROFILE_IMAGE_TIMESTAMP_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    // All profile images cleared
  } catch (error) {
    console.error('Error clearing profile images:', error);
  }
};

/**
 * Clear expired profile images to free up space
 */
export const clearOldProfileImages = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    
    keys.forEach(key => {
      if (key.startsWith(PROFILE_IMAGE_TIMESTAMP_PREFIX)) {
        const timestamp = localStorage.getItem(key);
        if (timestamp) {
          const age = now - parseInt(timestamp, 10);
          if (age > CACHE_DURATION_MS) {
            const userId = key.replace(PROFILE_IMAGE_TIMESTAMP_PREFIX, '');
            removeProfileImage(userId);
          }
        }
      }
    });
    
    // console.log('Old profile images cleared');');
  } catch (error) {
    console.error('Error clearing old profile images:', error);
  }
};

/**
 * Fetch and cache profile image from backend
 * @param userId - User identifier
 * @param forceRefresh - Force refresh even if cached
 * @returns Base64 encoded image data URL
 */
export const fetchAndCacheProfileImage = async (userId: string, forceRefresh = false): Promise<string | null> => {
  // fetchAndCacheProfileImage called
  try {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedImage = getProfileImage(userId);
      if (cachedImage) {
        // Using cached profile image for user
        return cachedImage;
      }
    }
    
    // Try the backend JSON endpoint that returns a base64 data URL (more reliable in restrictive browsers)
    try {
      const headers = await getProfileImageHeaders();
      if (!headers) {
        return null;
      }
      const jsonUrl = `/api/profile/profile-image-base64?_=${Date.now()}`;
      const resp = await fetch(jsonUrl, { credentials: 'include', headers });
      if (resp.ok) {
        const body = await resp.json();
        if (body && body.data_url) {
          saveProfileImage(userId, body.data_url);
          return body.data_url;
        }
      }
    } catch (err) {
      console.debug('profileImageCache: JSON endpoint failed, falling back to blob fetch', err);
    }

    return null;
  } catch (error) {
    console.error('Error fetching and caching profile image:', error);
    return null;
  }
};
